import React, { useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Play, Clock } from 'lucide-react';
import { useSound } from '../hooks/useSound';

const Portfolio = ({ data }) => {
  const [filter, setFilter] = useState('All');
  const { playClickSound } = useSound();
  const categories = ['All', ...new Set(data.map((item) => item.category))];

  const filteredData = filter === 'All' ? data : data.filter((item) => item.category === filter);

  const handleFilterClick = (category) => {
    playClickSound();
    setFilter(category);
  };

  return (
    <section id="portfolio" className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 space-y-4 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            Featured{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-yellow-500 bg-clip-text text-transparent">
              Portfolio
            </span>
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Explore a selection of my recent AI video projects
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-12 animate-fade-in-up delay-200">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => handleFilterClick(category)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 hover:scale-110 active:scale-95 ${
                filter === category
                  ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-lg shadow-emerald-500/30 scale-105'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-emerald-400'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredData.map((project, index) => {
            const delay = `delay-${Math.min(index * 100, 800)}`;
            return (
              <div key={project.id} className={`animate-scale-in ${delay}`}>
                <Card
                  className="group relative overflow-hidden bg-slate-800/50 border-slate-700 hover:border-emerald-500/50 transition-all duration-500 hover:shadow-xl hover:shadow-emerald-500/20 backdrop-blur-sm cursor-pointer hover:-translate-y-2 hover:scale-105"
              onClick={() => {
                playClickSound();
                if (project.videoUrl) {
                  const video = document.getElementById(`video-${project.id}`);
                  if (video) {
                    if (video.paused) {
                      video.play();
                    } else {
                      video.pause();
                    }
                  }
                }
              }}
            >
              {/* Thumbnail or Video - 9:16 Ratio */}
              <div className="relative overflow-hidden" style={{ aspectRatio: '9/16' }}>
                {project.videoUrl ? (
                  <video
                    id={`video-${project.id}`}
                    src={project.videoUrl}
                    className="w-full h-full object-cover"
                    controls
                    loop
                    playsInline
                  />
                ) : (
                  <>
                    <img
                      src={project.thumbnail}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                    
                    {/* Play Button */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center shadow-xl shadow-emerald-500/50 group-hover:scale-110 transition-transform">
                        <Play className="w-8 h-8 text-white fill-white" />
                      </div>
                    </div>
                  </>
                )}

                {/* Duration Badge */}
                <div className="absolute top-3 right-3">
                  <Badge className="bg-slate-950/80 text-white border-slate-700 backdrop-blur-sm flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {project.duration}
                  </Badge>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 space-y-3">
                <h3 className="text-base font-bold text-white group-hover:text-emerald-400 transition-colors">
                  {project.title}
                </h3>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {project.description}
                </p>
              </div>
            </Card>
          </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;